// A sentence end on the current text: terminal punctuation, optionally closed
// by a quote or bracket. Colons and semicolons introduce, not end, a sentence.
const SENTENCE_END = /[.!?…]["'”’)\]]?$/u
// A new sentence opening the added text: a capitalised word ("Before", "I",
// "A") and at least one more word. Uppercase tokens such as "COM/api" or
// "API_KEY" stay joined, as do lowercase and digit continuations.
const SENTENCE_START = /^["“‘(]?\p{Lu}(?:\p{Ll}|\s)/u
const HAS_SECOND_WORD = /\s\S/u

/**
 * Whether the input already ends with this append, boundary included: a
 * newline-led append counts only after that same newline, and a plain one
 * only as a whole trailing segment, so a new paragraph or a repeated word
 * is never mistaken for a retry.
 */
function endsWithAppend(current: string, text: string): boolean {
  const tail = current.trimEnd()
  if (/^\s/u.test(text)) {
    return tail.endsWith(text)
  }
  if (!tail.endsWith(text)) {
    return false
  }
  const before = tail.slice(0, tail.length - text.length)
  return before.length === 0 || /\s$/u.test(before)
}

/**
 * Add Sofia's `append_input_str` after a case's input: joined by a space
 * unless either side already brings whitespace (a leading newline starts a new
 * paragraph). An input that already ends with the text is returned unchanged,
 * so staging the same append twice, or retrying it, never doubles it.
 */
export function appendText(current: string, addition: string): string {
  const text = addition.trimEnd()
  if (text.trim().length === 0 || endsWithAppend(current, text)) {
    return current
  }
  if (current.length === 0) {
    return text.trimStart()
  }
  return /\s$/u.test(current) || /^\s/u.test(text)
    ? `${current}${text}`
    : `${current} ${text}`
}

/**
 * Restore the space Sofia dropped when it appended a sentence to an existing
 * value: "…practical help.Before hanging up…" becomes "…practical help. Before
 * hanging up…". Anything that is not the current value plus a run-on sentence
 * is returned unchanged, so re-staging the same proposal yields the same text.
 */
export function separateAppendedText(
  current: string | null | undefined,
  proposed: string,
): string {
  if (!current || proposed.length <= current.length) {
    return proposed
  }
  if (!proposed.startsWith(current)) {
    return proposed
  }
  const appended = proposed.slice(current.length)
  return SENTENCE_END.test(current) &&
    SENTENCE_START.test(appended) &&
    HAS_SECOND_WORD.test(appended)
    ? `${current} ${appended}`
    : proposed
}
